import React, { useEffect, useRef, useState } from "react";
import {
  Cpu, Database, HardDrive,
  RefreshCcw, Send, ShieldAlert, Sparkles,
  Wifi,
} from "lucide-react";
import { api } from "../lib/api";

import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import MetricDisplay from "../components/MetricDisplay";

// ---------------------------------------------------------------------------
// Theme + helpers
// ---------------------------------------------------------------------------
const STATUS = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700", left: "border-l-4 border-emerald-500", soft: "bg-emerald-50/70", border: "border-emerald-300" },
  amber: { bar: "bg-amber-500",   text: "text-amber-700",   chip: "bg-amber-100 text-amber-700",     left: "border-l-4 border-amber-500",   soft: "bg-amber-50/70",   border: "border-amber-300"   },
  red:   { bar: "bg-red-500",     text: "text-red-700",     chip: "bg-red-100 text-red-700",         left: "border-l-4 border-red-500",     soft: "bg-red-50/70",     border: "border-red-300"     },
  blue:  { bar: "bg-blue-500",    text: "text-blue-700",    chip: "bg-blue-100 text-blue-700",       left: "border-l-4 border-blue-500",    soft: "bg-blue-50/70",    border: "border-blue-300"    },
};

const CTO_DASH = "—";

function fmtAgo(hours) {
  if (hours === null || hours === undefined) return null;
  const h = Number(hours);
  if (Number.isNaN(h)) return null;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} minutes ago`;
  if (h < 24) return `${h.toFixed(1)} hours ago`;
  return `${Math.round(h / 24)} days ago`;
}

function Section({ title, subtitle, right, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AI Strategic Insights (real-only, no demo fallback)
// ---------------------------------------------------------------------------
function InsightCard({ insight }) {
  const s = STATUS[insight.severity] || STATUS.blue;
  return (
    <div data-testid={`cto-insight-${insight.id}`} className={`rounded-lg ${s.left} ${s.soft} p-4`}>
      <h4 className="text-[13.5px] font-bold text-[#0F1B2D]">{insight.title}</h4>
      <p className="mt-1 text-[12.5px] leading-snug text-slate-700">{insight.body}</p>
      <p className="mt-2 text-[12px] font-semibold text-emerald-700">{insight.action}</p>
    </div>
  );
}

function InsightsGrid({ dataAvailable = false }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("loading");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const res = await api.aiInsightsGenerate("cto", api.ctoInsights);
    setLoading(false);
    if (res.ok && res.data?.source === "ai" && Array.isArray(res.data.items) && res.data.items.length >= 3) {
      setItems(res.data.items); setSource("ai");
    } else {
      setItems([]); setSource("empty");
    }
  };

  useEffect(() => {
    refresh();
  }, [dataAvailable]);

  return (
    <section data-testid="cto-insights" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Executive Insights</div>
          <div className="text-[13px] text-slate-500">
            What your CTO would tell you today
            {source === "ai" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">C-ORE generated</span>}
          </div>
        </div>
        <button data-testid="cto-insights-refresh" onClick={refresh} disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11.5px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50">
          <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
          {loading ? "Generating…" : "Refresh insights"}
        </button>
      </div>
      {items.length === 0 ? (
        <div data-testid="cto-insights-empty" className="rounded-xl border border-dashed border-blue-200 bg-blue-50/50 px-4 py-8 text-center text-[12.5px] text-blue-700">
          {loading ? "Generating insights from your latest Tally data…" : "Connect Tally and run a sync to unlock personalised strategic insights."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-2">
          {items.map((it) => <InsightCard key={it.id} insight={it} />)}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// IT Spend Benchmark — real benchmark wiring is roadmap; for now show
// honest empty state with the active customer's name.
// ---------------------------------------------------------------------------
function SpendBenchmark({ companyName }) {
  const youLabel = companyName ? `${companyName} (you)` : "Your company (you)";
  return (
    <Section testid="cto-benchmark" title="IT Spend Benchmark" subtitle="Connect monthly IT spend in admin panel to enable this comparison">
      <ul className="space-y-2.5">
        <li data-testid="cto-bench-you" className="flex items-center gap-3">
          <span className="w-[100px] tablet:w-[150px] desktop:w-[180px] shrink-0 text-[12px] font-medium text-[#0F1B2D]">{youLabel}</span>
          <div className="h-2 flex-1 rounded-full bg-slate-100" />
          <span className="w-24 text-right text-[12px] font-semibold tabular-nums text-slate-400">{CTO_DASH}</span>
        </li>
        <li data-testid="cto-bench-peers" className="flex items-center gap-3">
          <span className="w-[100px] tablet:w-[150px] desktop:w-[180px] shrink-0 text-[12px] font-medium text-[#0F1B2D]">Industry peers (benchmark)</span>
          <div className="h-2 flex-1 rounded-full bg-slate-100" />
          <span className="w-24 text-right text-[12px] font-semibold tabular-nums text-slate-400">{CTO_DASH}</span>
        </li>
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Tally Health Monitor — real values from sync_info / cto / gst / inventory
// ---------------------------------------------------------------------------
function TallyHealth({ customerData }) {
  const sync   = customerData?.sync_info || {};
  const cto    = customerData?.cto       || {};
  const gst    = customerData?.gst       || {};
  const inv    = customerData?.inventoryAnalytics || {};

  const agentVersion   = sync.agent_version || cto.agent_version || null;
  const freshnessHours = cto.data_freshness_hours;
  const lastSyncTxt = (freshnessHours == null) ? null
    : (freshnessHours < 1 ? `${Math.max(1, Math.round(freshnessHours * 60))} minutes ago`
       : freshnessHours < 24 ? `${freshnessHours.toFixed(1)} hours ago`
       : `${Math.round(freshnessHours / 24)} days ago`);

  const gstinValue = gst?.gstin || null;
  const gstStatusValue = (gst?.gst_payable != null)
    ? `Output ₹${(Number(gst.gst_output_tax || 0) / 100000).toFixed(2)}L · Net ₹${(Number(gst.gst_payable) / 100000).toFixed(2)}L`
    : null;

  const itemCount = (Array.isArray(inv?.items_raw) ? inv.items_raw.length : null);

  const cells = [
    {
      id: "ver",
      label: "Tally agent version",
      note: agentVersion ? "Live from your installed agent" : "Awaiting first sync",
      value: agentVersion,
      tooltip: "Reported by the C-ORE Tally agent on each sync.",
    },
    {
      id: "sync",
      label: "Last Tally sync",
      note: freshnessHours != null ? "C-ORE agent → Supabase" : "Awaiting first sync",
      value: lastSyncTxt,
      tooltip: "Hours since your most recent successful Tally sync.",
    },
    {
      id: "uptime",
      label: "Sync uptime (30d)",
      note: cto.tally_uptime_pct != null ? "Successful syncs ÷ total syncs" : "Sync log builds over time",
      value: cto.tally_uptime_pct != null ? `${cto.tally_uptime_pct.toFixed(1)}%` : null,
      tooltip: "Percentage of successful syncs over the last 30 days.",
    },
    {
      id: "items",
      label: "Stock items synced",
      note: itemCount != null ? "Captured from your Tally stock ledger" : "Inventory snapshot pending",
      value: itemCount != null ? `${itemCount} items` : null,
      tooltip: "Number of inventory items in your latest Tally snapshot.",
    },
    {
      id: "gst",
      label: "GST status",
      note: gstinValue ? `GSTIN ${gstinValue}` : "GSTIN not captured in Tally sync",
      value: gstStatusValue,
      tooltip: "Live GST output, input and net payable from your Tally sync.",
    },
  ];

  return (
    <Section
      testid="cto-tally"
      title="Tally Health Monitor"
      subtitle="Your live Tally agent + sync log"
    >
      <ul className="divide-y divide-slate-100">
        {cells.map((c) => (
          <li key={c.id} data-testid={`cto-tally-${c.id}`} className="flex items-start justify-between gap-3 py-2.5">
            <div>
              <div className="text-[12.5px] font-medium text-[#0F1B2D]">{c.label}</div>
              <div className="text-[11.5px] text-slate-500">{c.note}</div>
            </div>
            <div className="text-right">
              {c.value
                ? <div className="text-[12.5px] font-semibold text-[#0F1B2D]">{c.value}</div>
                : <MetricDisplay value={null} format="raw" label={c.label} tooltipText={c.tooltip} size="small" />}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// CTO Chat
// ---------------------------------------------------------------------------
function CTOTyping() {
  return (
    <div data-testid="cto-chat-typing" className="max-w-[85%] rounded-2xl bg-[#EFF6FF] px-4 py-3">
      <div className="flex items-center gap-1">
        {[0, 200, 400].map((d) => (
          <span key={d} className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  );
}

function CTOChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef(`core-cto-${Math.random().toString(36).slice(2, 10)}`);
  const scroll = useRef(null);

  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, sending]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    const res = await api.ctoChat(t, sid.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <section data-testid="cto-chat" className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">Ask your CTO</div>
          <div className="text-[12px] text-slate-500">Software, security, automation · Hindi or English</div>
        </div>
        <Sparkles size={16} className="text-emerald-600" />
      </div>
      <div ref={scroll} className="space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 280 }}>
        {messages.length === 0 && !sending && (
          <div data-testid="cto-chat-empty" className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Ask anything about your software stack, security or automation. Try: &quot;How can I reduce my SaaS spend?&quot; or &quot;What should I audit this quarter?&quot;
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`cto-chat-msg-${i}`}
            className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${m.role === "ai" ? "bg-[#EFF6FF] text-[#0F1B2D]" : "ml-auto bg-emerald-600 text-white"}`}>
            {m.text}
          </div>
        ))}
        {sending && <CTOTyping />}
      </div>
      <div className="border-t border-slate-100 px-5 py-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input data-testid="cto-chat-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : "Ask about software, security, automation, or innovation"}
            disabled={sending}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-[#0F1B2D] placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
          />
          <button type="submit" data-testid="cto-chat-send" disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Active-customer module body (real data only — dash when missing)
// ---------------------------------------------------------------------------
function RealCtoKpi({ id, label, icon: Icon, value, format = "number", status = "blue", sub, tooltip }) {
  const s = STATUS[status] || STATUS.blue;
  return (
    <div data-testid={`cto-kpi-${id}`} className="relative overflow-hidden rounded-lg border border-[#DBEAFE] bg-white p-4 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 ${s.bar}`} />
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">{Icon && <Icon size={13} className={s.text} />}{label}</span>
      </div>
      <div className="mt-2">
        <MetricDisplay value={value} format={format} label={label} tooltipText={tooltip} size="large" />
      </div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

function CtoPlaceholder({ label, tooltip }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-slate-500 text-[11px] uppercase tracking-wider font-semibold">{label}</div>
      <div className="mt-1">
        <MetricDisplay value={null} format="raw" label={label} tooltipText={tooltip} size="medium" />
      </div>
    </div>
  );
}

function RealCtoBody({ customerData, dataAvailable, companyName }) {
  const cto    = customerData?.cto    || {};
  const params = customerData?.params || {};
  const sync   = customerData?.sync_info || {};

  const uptime      = cto.tally_uptime_pct;
  const freshness   = cto.data_freshness_hours;
  const itSpend     = params.it_spend_monthly ?? null;
  const itSpendPct  = cto.it_spend_ratio;
  const securityScore = params.security_score ?? null;
  const stack       = Array.isArray(cto.software_stack) ? cto.software_stack : null;
  const saasCount   = cto.saas_tools_count;
  const agentVer    = sync.agent_version || cto.agent_version || null;

  const uptimeStatus    = uptime === null ? "blue"
                        : uptime >= 99 ? "green"
                        : uptime >= 95 ? "amber"
                        : "red";
  const freshnessStatus = freshness === null ? "blue"
                        : freshness < 2 ? "green"
                        : freshness < 24 ? "amber"
                        : "red";

  return (
    <div className="space-y-5">
      {/* Section 1 — KPIs */}
      <section data-testid="cto-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-6">
        <RealCtoKpi id="uptime" label="Tally Uptime" icon={Wifi}
          value={uptime} format="percentage" status={uptimeStatus}
          sub={uptime !== null ? "rolling 30 days" : null}
          tooltip="Successful Tally syncs ÷ total syncs over the last 30 days." />
        <RealCtoKpi id="freshness" label="Data Freshness" icon={Database}
          value={fmtAgo(freshness)} format="raw" status={freshnessStatus}
          sub={freshness !== null ? "since last sync" : null}
          tooltip="Hours since your most recent Tally sync." />
        <RealCtoKpi id="agent_version" label="Agent Version" icon={Cpu}
          value={agentVer} format="raw" status={agentVer ? "green" : "blue"}
          sub={agentVer ? "Tally agent" : null}
          tooltip="Version of the C-ORE Tally agent currently running." />
        <RealCtoKpi id="it_spend" label="Monthly IT Spend" icon={HardDrive}
          value={itSpend} format="currency" status={itSpend === null ? "blue" : "amber"}
          sub={itSpend !== null ? "from your inputs" : null}
          tooltip="Enter monthly IT spend in admin panel to track this." />
        <RealCtoKpi id="it_pct" label="IT as % of Revenue" icon={HardDrive}
          value={itSpendPct} format="percentage" status={itSpendPct === null ? "blue" : itSpendPct > 5 ? "red" : "green"}
          sub={itSpendPct !== null ? "vs current revenue MTD" : null}
          tooltip="Requires both IT spend and a Tally revenue sync." />
        <RealCtoKpi id="security" label="Security Score" icon={ShieldAlert}
          value={securityScore} format="number"
          status={securityScore === null ? "blue" : securityScore >= 80 ? "green" : securityScore >= 60 ? "amber" : "red"}
          sub={securityScore !== null ? "0-100 audit score" : null}
          tooltip="Enter your IT security audit score in admin panel." />
      </section>

      {/* Section 2 — AI Strategic Insights */}
      <InsightsGrid dataAvailable={dataAvailable} />

      {/* Section 3 — Software stack */}
      <Section
        testid="cto-software-stack"
        title="Software Stack"
        subtitle={saasCount ? `${saasCount} tools captured` : "Add your tools in admin panel to enable monitoring"}
      >
        {Array.isArray(stack) && stack.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {stack.map((t, i) => (
              <span key={`stack-${i}`} data-testid={`cto-stack-${i}`} className="rounded-full bg-slate-100 px-3 py-1 text-[12px] font-medium text-slate-700">
                {t}
              </span>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-[12px] text-slate-500">
            <span className="text-slate-400 text-[18px] leading-none">{CTO_DASH}</span>
            <span>Enter your software stack (comma-separated) in Setup Checklist → Company Parameters.</span>
          </div>
        )}
      </Section>

      {/* Section 4 — Tech debt / monitoring placeholders */}
      <Section testid="cto-monitoring" title="Operations & Monitoring" subtitle="External integrations required">
        <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-4">
          <CtoPlaceholder label="Server response time" tooltip="Available once Railway monitoring is configured" />
          <CtoPlaceholder label="Cyber risk score"     tooltip="Enter in admin panel or connect a security scanner" />
          <CtoPlaceholder label="Tech debt score"      tooltip="Manual assessment required" />
          <CtoPlaceholder label="Innovation score"     tooltip="Based on quarterly review input" />
        </div>
      </Section>

      {/* Section 5 — IT Spend Benchmark (uses real company name) */}
      <SpendBenchmark companyName={companyName} />

      {/* Section 6 — Tally Health (real values for active customer) */}
      <TallyHealth customerData={customerData} />

      {/* CTO Chat */}
      <CTOChat />
    </div>
  );
}

export default function VirtualCTO() {
  const { customerData, dataAvailable, isLoading, companyName } = useCustomerData();
  return (
    <div data-testid="cto-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">CTO</div>
          <h2 className="text-[20px] font-bold text-[#1E3A5F]">{companyName ? `${companyName} — your tech, security & innovation co-pilot` : "Your tech, security & innovation co-pilot · Q4 FY26"}</h2>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[12px] text-slate-500">
          <Cpu size={13} className="text-emerald-600" />
          {"Tech stack inventory pending"}
        </div>
      </div>

      <RealCtoBody customerData={customerData} dataAvailable={dataAvailable} companyName={companyName} />
      </div>
    </div>
  );
}
